/** DTOs for patient portal MFA (OTP) — backend `AuthenticationController` `/auth/patient-mfa/*`. */

import { Tenant } from './tenant.model';

export interface PatientMfaContactOption {
  channelType: string;
  masked: string;
  label: string;
}

export interface PatientMfaOtpResponse {
  status: string;
  message?: string;
  npatientid?: string;
  sessionid?: string;
  destination?: string;
  contacts?: PatientMfaContactOption[];
  mfaVerified?: boolean;
  /** Backend signals zero usable contacts; show profile / unavailable UX. */
  mfaUnavailable?: boolean;
  /** Seconds until resend is allowed after max failed OTP validations. */
  retryAfterSeconds?: number;
  /** DB flag: true = blocked 5 min after 3 failed OTP attempts; false when cooldown expired. */
  otpBlocked?: boolean;
}

export interface PatientMfaSendOtpRequest {
  npatientid: string;
  tenantcode: string;
  channelType: string;
  /** Dial code without + (e.g. 91, 1). Overrides server MFA_SMS_COUNTRY_CODE when set. */
  smsCountryCode?: string;
  /** When true, backend loads rep contacts via WS_GetPatientRepresentativeDetails (nPRID). */
  patientRepresentative?: boolean;
}

export interface PatientMfaValidateOtpRequest {
  otp: string;
  npatientid: string;
  tenantcode: string;
}

export interface PatientMfaPreferenceResponse {
  status: string;
  message?: string;
  mfaEnabled?: boolean;
  introCompleted?: boolean;
  preferredChannel?: string;
}

export interface PatientMfaPreferenceRequest {
  npatientid: string;
  tenantcode: string;
  mfaEnabled?: boolean;
  introCompleted?: boolean;
  preferredChannel?: string;
}

export interface PatientMfaChannelRequest {
  npatientid: string;
  tenantcode: string;
  channelType: string;
  smsCountryCode?: string;
  patientRepresentative?: boolean;
}

export interface PatientMfaChannelValidationResponse {
  status: string;
  message?: string;
  channelType?: string;
  destination?: string;
  optStatus?: 'OPT_IN' | 'OPT_OUT' | 'UNKNOWN' | string;
  valid?: boolean;
}

/** Session payload between login and MFA completion (`pending_mfa_login`). */
export interface PendingMfaLogin {
  loginResponse?: unknown;
  tenantCode: string;
  currentTenant?: Tenant | null;
  userInfo: {
    sessionId: string;
    patientId?: string;
    prid?: string;
    patientName?: string;
    [key: string]: unknown;
  };
  decryptedTenantId?: string;
  hasAssociatedPatients?: boolean;
  associatedPatientsRaw?: unknown;
  npatientid: string;
  decryptedPermissions?: unknown;
  decryptedIconSettings?: unknown;
  decryptedOtherSettings?: unknown;
  isQuickActivated?: boolean;
  autoSendPreferredMfa: boolean;
  preferredMfaChannel: string | null;
  mfaPreference?: PatientMfaPreferenceResponse | null;
}

/** Persisted while patient is on the OTP entry screen (survives page reload). */
export interface MfaPendingOtpVerification {
  context: 'login' | 'profile-enable';
  npatientid: string;
  tenantCode: string;
  selectedChannel: string;
  maskedDestination: string;
  otpSentAtMs: number;
  resendEndsAtMs: number;
}

export const MFA_PENDING_OTP_VERIFY_KEY = 'mfa_pending_otp_verify';

/**
 * PE-6224 — queued MFA Audit Log rows during login MFA
 * (flushed after PortalSendIP for direct patients; on patient select for representatives).
 */
export const MFA_PENDING_AUDIT_EVENTS_KEY = 'pending_mfa_audit_events';

export interface PendingMfaAuditEvent {
  sActivityType: string;
  sDescription: string;
}
